import type { User } from "../types/api";
import { useQuery } from "@tanstack/react-query";
import api from "../api/client";
import { Link } from "react-router-dom";
import {
  ShieldExclamationIcon,
  EnvelopeIcon,
  DocumentCheckIcon,
} from "@heroicons/react/24/outline";

export default function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => {
      const res = await api.get("/admin/stats");
      return res.data.data;
    },
  });

  if (isLoading) return <div className="flex items-center justify-center min-h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  if (error) return <div className="text-red-500 dark:text-red-400 p-4">Error loading stats</div>;

  // Handle case where progressStats might not be available yet
  const progressStats = data?.progressStats || {
    current: {
      accountCreated: 0,
      emailConfirmed: 0,
      documentsUploaded: 0,
      bankingProvided: 0,
      contractStarted: 0,
      contractCompleted: 0,
      payoutScheduled: 0,
      payoutExecuted: 0,
    },
    deltas: {
      accountCreated: 0,
      emailConfirmed: 0,
      documentsUploaded: 0,
      bankingProvided: 0,
      contractStarted: 0,
      contractCompleted: 0,
      payoutScheduled: 0,
      payoutExecuted: 0,
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>

      {/* User Stats */}


      {/* KPI Tiles (Stage Deltas) */}
      <div>
        <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Key Performance Indicators (24h)</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
          <KPICard
            title="Account Created"
            value={progressStats.current.accountCreated}
            delta={progressStats.deltas.accountCreated}
          />
          <KPICard
            title="Email Confirmed"
            value={progressStats.current.emailConfirmed}
            delta={progressStats.deltas.emailConfirmed}
          />
          <KPICard
            title="Docs Uploaded"
            value={progressStats.current.documentsUploaded}
            delta={progressStats.deltas.documentsUploaded}
          />
          <KPICard
            title="Banking Done"
            value={progressStats.current.bankingProvided}
            delta={progressStats.deltas.bankingProvided}
          />
          <KPICard
            title="Contract Sent"
            value={progressStats.current.contractStarted}
            delta={progressStats.deltas.contractStarted}
          />
          <KPICard
            title="Signed/Done"
            value={progressStats.current.contractCompleted}
            delta={progressStats.deltas.contractCompleted}
          />
          <KPICard
            title="Payout Scheduled"
            value={progressStats.current.payoutScheduled}
            delta={progressStats.deltas.payoutScheduled}
          />
          <KPICard
            title="Payout Executed"
            value={progressStats.current.payoutExecuted}
            delta={progressStats.deltas.payoutExecuted}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Claimants by Stage (Chart) */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Claimants by Stage</h3>
          <div className="space-y-4">
            {[
              { key: 'accountCreated', name: 'Account Created' },
              { key: 'emailConfirmed', name: 'Email Confirmed' },
              { key: 'documentsUploaded', name: 'Documents Uploaded' },
              { key: 'bankingProvided', name: 'Banking Provided' },
              { key: 'contractStarted', name: 'Contract Started' },
              { key: 'contractCompleted', name: 'Contract Completed' },
              { key: 'payoutScheduled', name: 'Payout Scheduled' },
              { key: 'payoutExecuted', name: 'Payout Executed' }
            ].map((stage) => {
              const count = progressStats.current[stage.key];
              const total = data.stats?.totalUsers || data.totalUsers || 1; // Avoid div by zero
              const percentage = ((count / total) * 100).toFixed(1);
              return (
                <div key={stage.key}>
                  <div className="flex items-center justify-between text-sm font-medium">
                    <span className="text-gray-500 dark:text-gray-400">{stage.name}</span>
                    <span className="text-gray-900 dark:text-white">{count} users ({percentage}%)</span>
                  </div>
                  <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 dark:bg-blue-500 h-2.5 rounded-full"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>


        {/* Right column: Pending Issues + Recent Verifications */}
        <div className="space-y-5">
          {/* Pending Issues */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Pending Issues</h3>
            <div className="flow-root">
              <ul role="list" className="-my-5 divide-y divide-gray-200 dark:divide-gray-700">
                {data.pendingIssues?.map((user: User) => {
                  const riskLevel = user.riskLevel;
                  const docusignError = user.docusign?.deliveryStatus === 'failed' || user.docusign?.deliveryStatus === 'autoresponded' || user.docusign?.deliveryStatus === 'authfailed';
                  const postmarkError = user.postmark?.deliveryStatus === 'bounced' || user.postmark?.deliveryStatus === 'spam_complaint';
                  return (
                    <li key={user._id} className="py-3">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center gap-1">
                          {(riskLevel === 'critical' || riskLevel === 'high' || riskLevel === 'medium') && (
                            <ShieldExclamationIcon
                              className={`h-5 w-5 ${riskLevel === 'critical' ? 'text-red-600' : riskLevel === 'high' ? 'text-red-500' : 'text-yellow-500'}`}
                              title={`${riskLevel} fraud risk`}
                            />
                          )}
                          {docusignError && (
                            <DocumentCheckIcon className="h-5 w-5 text-orange-500" title="DocuSign issue" />
                          )}
                          {postmarkError && (
                            <EnvelopeIcon className="h-5 w-5 text-orange-500" title="Email delivery issue" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{user.name || user.email}</p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {riskLevel && `Fraud: ${riskLevel}`}
                            {riskLevel && (docusignError || postmarkError) && ' • '}
                            {docusignError && `DocuSign: ${user.docusign?.deliveryStatus}`}
                            {docusignError && postmarkError && ' • '}
                            {postmarkError && `Email: ${user.postmark?.deliveryStatus}`}
                          </p>
                        </div>
                        <div>
                          <Link
                            to={`/claimants/${user._id}`}
                            className="inline-flex items-center rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2.5 py-0.5 text-sm font-medium leading-5 text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    </li>
                  );
                })}
                {(!data.pendingIssues || data.pendingIssues.length === 0) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No pending issues.</p>
                )}
              </ul>
            </div>
          </div>

          {/* Recent Verifications */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">Recent Verifications</h3>
            <div className="flow-root">
              <ul role="list" className="-my-5 divide-y divide-gray-200 dark:divide-gray-700">
                {data.recentVerifications.slice(0, 3).map((user: User) => (
                  <li key={user._id} className="py-3">
                    <div className="flex items-center space-x-4">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{user.name || user.email}</p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {user.progress?.completedAt?.contractCompleted ? new Date(user.progress.completedAt.contractCompleted).toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                      <div>
                        <Link
                          to={`/claimants/${user._id}`}
                          className="inline-flex items-center rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2.5 py-0.5 text-sm font-medium leading-5 text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          View
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
                {data.recentVerifications.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No recent verifications.</p>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICard({ title, value, delta, highlight = false }: { title: string; value: number; delta: number; highlight?: boolean }) {
  const isPositive = delta > 0;
  return (
    <div className={`overflow-hidden rounded-lg px-4 py-5 shadow sm:p-6 ${highlight ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-200 dark:ring-indigo-900' : 'bg-white dark:bg-gray-800'}`}>
      <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">{title}</dt>
      <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-baseline gap-2">
        {value}
        {delta !== 0 && (
          <span className={`text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? '↑' : '↓'} {Math.abs(delta)}
          </span>
        )}
        {delta === 0 && <span className="text-sm font-medium text-gray-400">-</span>}
      </dd>
    </div>
  );
}
